import type { JoinerySpec } from '@bw/document';
import { wallToWorld } from './transform.js';
import type { BuildingOpening, PlacedWall, Vec3 } from './types.js';

/**
 * Windows and doors, generated from the openings (phase 2 decision).
 *
 * Parts come out in exactly the form a brick does — a centre, half-extents in
 * the wall's own frame, and the wall's rotation — so the renderer draws them
 * with the same instancing it already has and needs no new pipeline.
 *
 * Only a rectangular opening gets joinery. An arched or irregular one stays an
 * honest hole in the brickwork, which is the recorded decision rather than a
 * limitation discovered later.
 */

export type JoineryKind = 'frame' | 'glass' | 'sill' | 'leaf';

export interface JoineryPart {
  readonly kind: JoineryKind;
  readonly wallId: string;
  readonly shapeId: string;
  readonly centre: Vec3;
  readonly half: Vec3;
  readonly rotationDeg: number;
}

/** How far a sill projects past the face of the wall. */
const SILL_PROJECTION_MM = 30;
/** However deep the reveal, this much of the wall is left for the frame. */
const MIN_FRAME_DEPTH_MM = 20;
const SILL_THICKNESS_MM = 40;

/**
 * The parts for one opening.
 *
 * The frame sits `revealMm` back from the outer face — the face you drew — and
 * is `frameMm` wide all round. Glazing sits `glassInsetMm` further in again. A
 * door gets a leaf in place of the glass and no sill.
 */
export function joineryFor(
  wall: PlacedWall,
  opening: BuildingOpening,
  spec: JoinerySpec,
): JoineryPart[] {
  if (!opening.rectangular) return [];
  const thickness = wall.placement.thicknessMm;
  const width = opening.width;
  const height = opening.height;
  const frame = Math.max(1, Math.min(spec.frameMm, width / 2 - 1, height / 2 - 1));
  if (width <= 0 || height <= 0) return [];

  // Depths are measured from the outer face at local z = 0, inward. The reveal
  // is limited by the DEPTH left for the frame, not by the frame's width —
  // those are different dimensions, and confusing them quietly pulls the frame
  // forward out of its reveal.
  const reveal = Math.min(
    Math.max(0, spec.revealMm),
    Math.max(0, thickness - MIN_FRAME_DEPTH_MM),
  );
  const frameDepth = Math.max(MIN_FRAME_DEPTH_MM, Math.min(frame, thickness - reveal));
  const frameZ = reveal + frameDepth / 2;
  const glassZ = Math.min(thickness - 5, frameZ + spec.glassInsetMm);

  const parts: JoineryPart[] = [];
  const push = (
    kind: JoineryKind,
    x: number,
    y: number,
    z: number,
    hx: number,
    hy: number,
    hz: number,
  ): void => {
    if (hx <= 0 || hy <= 0 || hz <= 0) return;
    parts.push({
      kind,
      wallId: wall.id,
      shapeId: opening.shapeId,
      centre: wallToWorld(wall.placement, x, y, z),
      half: { x: hx, y: hy, z: hz },
      rotationDeg: wall.placement.rotationDeg,
    });
  };

  const left = opening.x;
  const bottom = opening.y;
  const centreX = left + width / 2;
  const centreY = bottom + height / 2;
  const halfDepth = frameDepth / 2;

  // Head and cill members run the full width; jambs fill between them.
  push('frame', centreX, bottom + frame / 2, frameZ, width / 2, frame / 2, halfDepth);
  push('frame', centreX, bottom + height - frame / 2, frameZ, width / 2, frame / 2, halfDepth);
  const jambHeight = height - frame * 2;
  if (jambHeight > 0) {
    push('frame', left + frame / 2, centreY, frameZ, frame / 2, jambHeight / 2, halfDepth);
    push('frame', left + width - frame / 2, centreY, frameZ, frame / 2, jambHeight / 2, halfDepth);
  }

  const paneWidth = width - frame * 2;
  const paneHeight = height - frame * 2;
  if (paneWidth > 0 && paneHeight > 0) {
    if (opening.role === 'door') {
      push('leaf', centreX, centreY, glassZ, paneWidth / 2, paneHeight / 2, 20);
    } else {
      push('glass', centreX, centreY, glassZ, paneWidth / 2, paneHeight / 2, 3);
    }
  }

  if (opening.role === 'window') {
    // The sill projects past the outer face, which is what throws water clear.
    push(
      'sill',
      centreX,
      bottom - SILL_THICKNESS_MM / 2,
      (reveal - SILL_PROJECTION_MM) / 2,
      width / 2 + frame,
      SILL_THICKNESS_MM / 2,
      (reveal + SILL_PROJECTION_MM) / 2,
    );
  }

  return parts;
}

/** Every generated part in the building. */
export function joineryParts(
  walls: readonly PlacedWall[],
  openings: readonly BuildingOpening[],
  spec: JoinerySpec,
): JoineryPart[] {
  const byId = new Map(walls.map((wall) => [wall.id, wall]));
  const out: JoineryPart[] = [];
  for (const opening of openings) {
    const wall = byId.get(opening.wallId);
    if (wall === undefined) continue;
    out.push(...joineryFor(wall, opening, spec));
  }
  return out;
}
